<template>
  <div>
    <div v-if="uiType === 'rectangle'">
      <div
        class="flex flex-col justify-center items-center"
        @dragover.prevent="onDragOver"
        @dragenter.prevent="onDragEnter"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
        :class="{
          'opacity-90': isDragging,
          'cursor-not-allowed': !isMultiple && hasFile,
          'cursor-pointer': !(!isMultiple && hasFile),
        }"
      >
        <input
          :id="field.id"
          type="file"
          :accept="accept"
          :multiple="isMultiple"
          @change="onFileChange"
          :disabled="field.readOnly === 'yes' || (!isMultiple && hasFile)"
          class="hidden"
          ref="fileInput"
          aria-label="$t('commonLabels.uploadPhoto')"
        />
        <button
          type="button"
          @click="triggerFileInput"
          class="bg-transparent border-none p-0 flex flex-row items-center justify-center text-center gap-2"
          :class="{
            'cursor-not-allowed opacity-50': !isMultiple && hasFile,
            'cursor-pointer': !(!isMultiple && hasFile),
          }"
          :disabled="field.readOnly === 'yes' || (!isMultiple && hasFile)"
        >
          <span
            v-html="
              fileUpload({
                width: 40,
                height: 40,
                color: !isMultiple && hasFile ? '#ccc' : '#05A8F4',
              })
            "
          ></span>
          <span
            class="text-[20px] font-medium"
            :class="{
              'text-gray-400': !isMultiple && hasFile,
              'text-black': !(!isMultiple && hasFile),
            }"
          >
            {{
              !isMultiple && hasFile
                ? $t("commonLabels.fileAlreadyUploaded")
                : $t("commonLabels.chooseFile")
            }}
          </span>
        </button>

        <!-- Format description -->
        <p
          v-if="showFormatDescription && formatDescription"
          class="text-gray-500 text-sm mt-2"
        >
          {{ formatDescription }}
        </p>
      </div>

      <!-- Error Message for Rectangle UI -->
      <div v-if="hasError" class="mt-2 text-red-500 text-sm">
        <div
          v-for="errorMsg in errorMessages"
          :key="errorMsg"
          class="error-message"
        >
          {{ errorMsg }}
        </div>
      </div>

      <div v-if="isMultiple">
        <div
          v-for="(item, idx) in displayList"
          :key="item.fileId || idx"
          class="file-list-item"
        >
          <!-- Separator line (except for first item) -->
          <div v-if="idx > 0" class="border-t border-gray-200 my-3"></div>

          <div
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <div class="flex items-center gap-3">
              <!-- File type icon -->
              <div
                class="w-8 h-8 flex items-center justify-center rounded"
                :class="getFileIconColor(item.name || item.file?.name).bg"
              >
                <span
                  class="font-bold text-[10px] uppercase"
                  :class="getFileIconColor(item.name || item.file?.name).text"
                >
                  {{ getFileExtension(item.name || item.file?.name) }}
                </span>
              </div>

              <!-- File info -->
              <div class="flex flex-col">
                <span class="text-gray-900 text-[20px]">{{
                  item.name || item.file?.name
                }}</span>
                <div class="flex items-center gap-2 text-xs text-gray-500">
                  <span
                    >{{ getFileSize(item) }} of {{ getFileSize(item) }}</span
                  >

                  <!-- Status indicator -->
                  <div class="flex items-center gap-1 ml-1">
                    <div
                      v-if="getStatus(item) === 'uploading'"
                      class="w-2 h-2 rounded-full bg-blue-500"
                    ></div>
                    <div
                      v-else-if="getStatus(item) === 'uploaded'"
                      class="w-2 h-2 rounded-full bg-green-500"
                    ></div>
                    <span
                      v-if="getStatus(item) === 'uploading'"
                      class="text-blue-500 text-xs"
                      >{{ $t("commonLabels.uploading") }}</span
                    >
                    <span
                      v-else-if="getStatus(item) === 'uploaded'"
                      class="text-green-500 text-xs"
                      >{{ $t("commonLabels.uploaded") }}</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-2">
              <button
                @click="removeFile(idx)"
                class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-red-500 hover:text-red-600 transition-colors"
                :title="$t('commonLabels.removeFile')"
              >
                <span v-html="trashLineIcon"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="fileObject"
        class="mt-3 flex items-center justify-between p-3 bg-gray-50 rounded-lg"
      >
        <div class="flex items-center gap-3">
          <!-- File type icon -->
          <div
            class="w-8 h-8 flex items-center justify-center rounded"
            :class="getFileIconColor(getFileName(fileObject)).bg"
          >
            <span
              class="font-bold text-[10px] uppercase"
              :class="getFileIconColor(getFileName(fileObject)).text"
            >
              {{ getFileExtension(getFileName(fileObject)) }}
            </span>
          </div>

          <!-- File info -->
          <div class="flex flex-col">
            <span class="font-medium text-black text-[20px] font-inter">{{
              getFileName(fileObject)
            }}</span>
            <div
              class="flex items-center gap-2 font-normal text-grey text-[16px] font-inter"
            >
              <span
                >{{ getFileSize(fileObject) }} of
                {{ getFileSize(fileObject) }}</span
              >

              <!-- Status indicator -->
              <div class="flex items-center gap-1 ml-1">
                <span v-html="checksIcon"></span>
                <span
                  class="font-medium text-darkGreen text-[12px] font-just leading-normal"
                  >{{ $t("commonLabels.uploading") }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="flex items-center gap-2">
          <button
            @click="removeFile(0)"
            class="w-8 h-8 flex items-center justify-center rounded-full border-none cursor-pointer"
            :title="$t('commonLabels.removeFile')"
          >
            <span v-html="trashLineIcon"></span>
          </button>
        </div>
      </div>
    </div>

    <div v-else-if="uiType === 'round'">
      <div
        class="flex flex-col justify-center items-center gap-1 rounded-full w-[80px] h-[80px] laptop:w-[160px] laptop:h-[155px] bg-[linear-gradient(288deg,#F9FFFF_6.06%,#C0EEF5_101.45%)] relative"
      >
        <input
          :id="field.id"
          type="file"
          :accept="accept"
          :multiple="isMultiple"
          @change="onFileChange"
          @blur="handleBlur"
          :disabled="field.readOnly === 'yes'"
          class="hidden"
          ref="fileInput"
          aria-label="$t('commonLabels.uploadPhoto')"
        />
        <div v-if="isMultiple">
          <div
            v-for="(item, idx) in filesArray"
            :key="item.fileId || idx"
            class="image-preview relative"
          >
            <img
              :src="getImageUrl(item)"
              alt="User Avatar"
              class="preview-img rounded-full w-full h-full object-cover"
            />
            <button
              @click="removeFile(idx)"
              class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
            >
              ×
            </button>
          </div>
        </div>
        <div
          v-else-if="fileObject || previewUrl"
          class="relative w-full h-full flex items-center justify-center"
        >
          <BaseSkeleton
            v-if="loading"
            :width="'100%'"
            :height="'100%'"
            :shape="'circle'"
            class="absolute inset-0 z-10"
          />
          <img
            v-if="!loading"
            :src="previewUrl || getImageUrl(fileObject)"
            class="preview-img rounded-full object-cover w-full h-full cursor-pointer"
            style="width: 100%; height: 100%"
            alt="User Avatar"
          />
          <button
            v-if="!loading"
            @click="triggerFileInput"
            class="absolute bottom-1 right-1 bg-white rounded-full border-none shadow p-2 flex items-center justify-center"
            style="width: 40px; height: 40px"
            :title="$t('commonLabels.changePhoto')"
            :disabled="field.readOnly === 'yes'"
          >
            <span
              class="flex items-center"
              v-html="profilePhotoChangeSvg({ width: 24, height: 24 })"
            ></span>
          </button>
        </div>
        <div v-else>
          <button
            @click="triggerFileInput"
            class="bg-transparent border-none p-0 flex flex-col items-center justify-center text-center cursor-pointer"
            :disabled="field.readOnly === 'yes'"
          >
            <span
              v-html="imgUpload({ width: iconSize, height: iconSize })"
            ></span>
            <span
              class="text-tealBlue text-[8px] laptop:text-[16px] font-medium"
              >{{ $t("commonLabels.uploadPhoto") }}</span
            >
          </button>
        </div>
      </div>

      <!-- Error Message for Round UI -->
      <!-- FIXED: Removed duplicate condition -->
      <div v-if="hasError">
        <div v-for="errorMsg in errorMessages" :key="errorMsg">
          <Message
            v-if="invalid && errorMessage"
            severity="error"
            size="small"
            variant="simple"
            :class="{
              '!text-error !first-letter:uppercase !first-letter:tracking-wider !px-[10px] !mt-[3.84px] !font-robo !font-400 !text-[15.36px]':
                invalid,
            }"
            :data-test-id="`${id}-errorMessage`"
          >
            {{ errorMsg }}
          </Message>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  fileUpload,
  imgUpload,
  profilePhotoChangeSvg,
  trashLineIcon,
  checksIcon,
} from "@/utils/commonSvg";
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import i18n from "@/i18n";
import BaseSkeleton from "./BaseSkeleton.vue";

const props = defineProps({
  field: { type: Object, required: true },
  modelValue: { type: [File, Array, null, Number, Object], default: null },
  error: { type: String, default: "" },
  errorMessage: { type: String, default: "" },
  errors: { type: Array, default: () => [] },
  invalid: { type: Boolean, default: false },
  isMultiple: { type: Boolean, default: false },
  uiType: {
    type: String,
    default: "rectangle",
    validator: (value) => ["round", "rectangle"].includes(value),
  },
  accept: { type: String, default: "" },
  statusList: { type: Array, default: () => [] },
  previewUrl: { type: String, default: "" },
  loading: { type: Boolean, default: false },
  // New props for validation
  enableValidation: { type: Boolean, default: false },
  maxFileSize: { type: Number, default: 52428800 }, // 50MB in bytes
  allowedFormats: {
    type: Array,
    default: () => ["png", "jpg", "jpeg", "pdf"],
  },
  showFormatDescription: { type: Boolean, default: false },
  formatDescription: { type: String, default: "" },
});

const emit = defineEmits([
  "update:modelValue",
  "add",
  "remove",
  "cancel",
  "validation-error",
]);

const fileInput = ref(null);
const iconSize = ref(window.innerWidth >= 1024 ? 46 : 24);
const isDragging = ref(false);
const validationErrors = ref([]);
const uploadedFileInfo = ref(null);

// NEW: Track if user has interacted with this field
// This prevents showing validation errors on initial load
const isTouched = ref(false);

// NEW: Handle blur event to mark field as touched
const handleBlur = () => {
  isTouched.value = true;
};

// NEW: Mark field as touched when user interacts with it
// This allows validation errors to show after user interaction
const markAsTouched = () => {
  isTouched.value = true;
};

// Computed property to check if file exists for single upload
const hasFile = computed(() => {
  if (props.isMultiple) {
    return false;
  }
  const value = props.modelValue;
  if (uploadedFileInfo.value) {
    return true;
  }
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  ) {
    return false;
  }
  
  return (
    value instanceof File ||
    typeof value === "number" ||
    (typeof value === "object" &&
      (value.fileId || value.file || value.url || value.name))
  );
});

// File validation functions
function validateFile(file) {
  const errors = [];

  if (!props.enableValidation) {
    return errors;
  }

  // Check file size
  if (file.size > props.maxFileSize) {
    const maxSizeMB = Math.round(props.maxFileSize / 1024 / 1024);
    errors.push(i18n.global.t("documentLabels.fileSizeError", { size: maxSizeMB }));
  }

  // Check file format
  const fileExtension = getFileExtension(file.name).toLowerCase();

  // Check both file extension and MIME type for better validation
  const isValidFormat = props.allowedFormats.some((format) => {
    const formatLower = format.toLowerCase();
    return (
      fileExtension === formatLower ||
      file.type.includes(formatLower) ||
      (formatLower === "jpg" && file.type.includes("jpeg"))
    );
  });

  if (!isValidFormat) {
    const formatsText = props.allowedFormats.join(", ").toUpperCase();
    errors.push(i18n.global.t("documentLabels.fileFormatError", { formats: formatsText }));
  }

  return errors;
}

function getFileExtension(filename) {
  if (!filename) return "FILE";
  const extension = filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
  return extension.toUpperCase() || "FILE";
}

function getFileIconColor(filename) {
  if (!filename) return { bg: "bg-gray-100", text: "text-gray-600" };

  const extension = getFileExtension(filename).toLowerCase();

  switch (extension) {
    case "pdf":
      return { bg: "bg-red-100", text: "text-red-600" };
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
      return { bg: "bg-green-100", text: "text-green-600" };
    case "doc":
    case "docx":
      return { bg: "bg-blue-100", text: "text-blue-600" };
    case "xls":
    case "xlsx":
      return { bg: "bg-green-100", text: "text-green-600" };
    case "ppt":
    case "pptx":
      return { bg: "bg-orange-100", text: "text-orange-600" };
    case "zip":
    case "rar":
    case "7z":
      return { bg: "bg-yellow-100", text: "text-yellow-600" };
    default:
      return { bg: "bg-gray-100", text: "text-gray-600" };
  }
}

function validateFiles(files) {
  const errors = [];

  if (!props.enableValidation) {
    return errors;
  }

  for (const file of files) {
    const fileErrors = validateFile(file);
    errors.push(...fileErrors);
  }

  return [...new Set(errors)]; // Remove duplicates
}

// Computed property to determine if there are errors
// NEW: Check for required field without file
const hasError = computed(() => {
  // Don't show errors until user has interacted with the field
  if (!isTouched.value) return false;

  // Check if field is required and has no file
  const isRequired = props.field?.isRequired === "yes";
  const hasNoFile = !hasFile.value;

  // Show error if required field has no file and is touched
  if (isRequired && hasNoFile && isTouched.value) {
    return true;
  }

  return (
    props.invalid ||
    Boolean(props.error) ||
    Boolean(props.errorMessage) ||
    (props.errors && props.errors.length > 0) ||
    (validationErrors.value && validationErrors.value.length > 0)
  );
});

// Computed property to get all error messages
const errorMessages = computed(() => {
  const messages = [];

  if (props.error) messages.push(props.error);
  if (props.errorMessage) messages.push(props.errorMessage);
  if (props.errors && props.errors.length > 0) {
    messages.push(...props.errors);
  }
  if (validationErrors.value && validationErrors.value.length > 0) {
    messages.push(...validationErrors.value);
  }

  // NEW: Add required field error if field is touched, required, and empty
  const isRequired = props.field?.isRequired === "yes";
  const hasNoFile = !hasFile.value;
  if (isRequired && hasNoFile && isTouched.value && messages.length === 0) {
    const fieldName =
      props.field?.displayTitle || props.field?.fieldName || "File";
    messages.push(i18n.global.t("documentLabels.fieldRequired", { field: fieldName }));
  }

  // Remove duplicates and filter empty strings
  return [...new Set(messages.filter((msg) => msg && msg.trim()))];
});

// Watch for external error changes and clear validation errors
watch(
  () => [props.error, props.errorMessage, props.errors],
  () => {
    // Only clear validation errors if external errors exist
    if (
      props.error ||
      props.errorMessage ||
      (props.errors && props.errors.length > 0)
    ) {
      validationErrors.value = [];
    }
  },
  { deep: true }
);

function handleResize() {
  iconSize.value = window.innerWidth >= 1024 ? 46 : 24;
}

function getImageUrl(file) {
  if (!file && props.previewUrl) return props.previewUrl;
  if (!file) return "";
  if (typeof file === "string") return file;
  if (file.url) return file.url;
  if (typeof file === "number") return "";
  if (file.file) return URL.createObjectURL(file.file);
  if (file instanceof File) return URL.createObjectURL(file);
  return "";
}

function getFileName(file) {
  if (!file) return "";

  if (uploadedFileInfo.value?.name) {
    return uploadedFileInfo.value.name;
  }

  if (typeof file === "string") return file;
  if (file.name) return file.name;
  if (file.file?.name) return file.file.name;
  if (typeof file === "number" && props.field?.fileName) {
    return props.field.fileName;
  }

  return "Uploaded file";
}

const filesArray = computed(() => {
  if (props.isMultiple) {
    return Array.isArray(props.modelValue) ? props.modelValue : [];
  }
  return props.modelValue ? [props.modelValue] : [];
});

const fileObject = computed(() => {
  if (props.isMultiple) return null;

  if (uploadedFileInfo.value) {
    return uploadedFileInfo.value;
  }

  const value = props.modelValue;

  if (!value) return null;

  if (value instanceof File) {
    return { file: value, name: value.name, size: value.size };
  }

  if (typeof value === "number") {
    return {
      fileId: value,
      name: props.field?.fileName || "Uploaded file",
      size: props.field?.fileSize || 0,
    };
  }

  if (typeof value === "object") {
    return value;
  }

  return null;
});

function triggerFileInput() {
  if (props.field.readOnly === "yes") return;
  // NEW: Mark as touched when user clicks to upload
  isTouched.value = true;
  fileInput.value && fileInput.value.click();
}

function onFileChange(event) {
  const files = Array.from(event.target.files);

  // NEW: Mark as touched when file is selected
  isTouched.value = true;

  validationErrors.value = [];

  const errors = validateFiles(files);

  if (errors.length > 0) {
    validationErrors.value = errors;
    emit("validation-error", errors);
    event.target.value = "";
    return;
  }

  const file = files[0];
  if (!props.isMultiple && file) {
    uploadedFileInfo.value = {
      file: file,
      name: file.name,
      size: file.size,
      status: "uploading",
    };
  }

  if (props.isMultiple) {
    emit("add", files);
    emit("update:modelValue", [...filesArray.value, ...files]);
  } else {
    emit("add", file);
    emit("update:modelValue", file);
  }

  // Clear the input value to allow uploading the same file again
  event.target.value = "";
}

function onDrop(event) {
  isDragging.value = false;

  // For single file upload, prevent if file already exists
  if (!props.isMultiple && hasFile.value) {
    return;
  }

  // NEW: Mark as touched when file is dropped
  isTouched.value = true;

  const files = Array.from(event.dataTransfer.files);

  // Clear previous validation errors
  validationErrors.value = [];

  // Validate files
  const errors = validateFiles(files);

  if (errors.length > 0) {
    validationErrors.value = errors;
    emit("validation-error", errors);
    return;
  }

  const file = files[0];

  // Store file information
  if (!props.isMultiple && file) {
    uploadedFileInfo.value = {
      file: file,
      name: file.name,
      size: file.size,
      status: "uploading",
    };
  }

  if (props.isMultiple) {
    emit("add", files);
    emit("update:modelValue", [...filesArray.value, ...files]);
  } else {
    emit("add", file);
    emit("update:modelValue", file);
  }
}

function onDragOver(event) {
  event.preventDefault();
  // Don't allow drag over if single file and file exists
  if (!props.isMultiple && hasFile.value) {
    event.dataTransfer.dropEffect = "none";
  }
}

function onDragEnter(event) {
  event.preventDefault();
  // Don't show drag effect if single file and file exists
  if (!props.isMultiple && hasFile.value) {
    return;
  }
  isDragging.value = true;
}

function onDragLeave(event) {
  event.preventDefault();
  isDragging.value = false;
}

function removeFile(idx) {
  // Clear validation errors when removing file
  validationErrors.value = [];
  uploadedFileInfo.value = null;

  if (props.isMultiple) {
    const updated = [...filesArray.value];
    const removed = updated.splice(idx, 1)[0];
    emit("remove", removed);
    emit("update:modelValue", updated);
  } else {
    emit("remove", fileObject.value);
    emit("update:modelValue", null);
  }
}

function getStatus(item) {
  return item.status || "uploaded";
}

const displayList = computed(() => {
  if (props.isMultiple) return filesArray.value;
  return fileObject.value ? [fileObject.value] : [];
});

function getFileSize(file) {
  if (!file) return "";

  let size = file.size || file.file?.size || 0;

  if (size === 0) return "Unknown size";

  const kb = size / 1024;
  if (kb < 1024) return Math.round(kb) + " KB";
  const mb = kb / 1024;
  return mb.toFixed(1) + " MB";
}

// Watch for when upload completes (modelValue changes to fileId)
watch(
  () => props.modelValue,
  (newVal, oldVal) => {
    // If modelValue changed from File to number (fileId), update status
    if (oldVal instanceof File && typeof newVal === "number") {
      if (uploadedFileInfo.value) {
        uploadedFileInfo.value.status = "uploaded";
        uploadedFileInfo.value.fileId = newVal;
      }
    }

    // If modelValue becomes null, clear stored info
    if (!newVal) {
      uploadedFileInfo.value = null;
    }

    // NEW: If we have a value (file uploaded or existing file), mark as touched
    // This handles the case when editing an existing record with a file
    if (newVal) {
      isTouched.value = true;
    }
  }
);

onMounted(() => {
  window.addEventListener("resize", handleResize);

  // Initialize uploadedFileInfo if we have a fileId on mount
  if (!props.isMultiple && typeof props.modelValue === "number") {
    uploadedFileInfo.value = {
      fileId: props.modelValue,
      name: props.field?.fileName || "Uploaded file",
      size: props.field?.fileSize || 0,
      status: "uploaded",
    };
    // NEW: Mark as touched if editing with existing file
    // This prevents showing errors for already uploaded files
    isTouched.value = true;
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});
</script>
